import { withPrefix } from '@/lib/leadIntake';
import { arCount, type PortalLang } from '@/lib/lang';

/**
 * The front door's words in Hebrew, Arabic and English. Arabic is Modern
 * Standard Arabic, addressed in the plural (like the Hebrew "אתם"), warm and
 * short. The privacy notice says the same in each language: it is versioned on
 * the server (PORTAL_LEAD_CONSENT_VERSION in portal-lead.service.ts), and the
 * language shown is stored with the consent. Change the meaning in one
 * language and you change it in all three, and bump that version.
 */
export type Situation = 'prospect' | 'resident' | 'expired' | 'invalid';

export interface FrontDoorStrings {
  situations: Record<Situation, { title: string; lead: string }>;
  or: string;
  googleErrors: Record<string, string>;
  googleFailed: string;
  network: string;
  tooMany: string;
  botFailed: string;
  noticeChanged: string;
  sendFailed: string;
  emailMissing: string;
  emailMalformed: string;
  botPending: string;
  sentTitle: string;
  sentProspect: string;
  sentResident: string;
  sentOpen: string;
  sentSpam: string;
  sentAgain: string;
  resendIn: (seconds: number) => string;
  resend: string;
  changeEmail: string;
  emailLabel: string;
  didYouMean: string;
  didYouMeanEnd: string;
  marketing: (operator: string) => string;
  sending: string;
  submit: string;
  alreadyLive: string;
  opening: string;
  secure: string;
  privacy: {
    short: (operator: string) => string;
    more: string;
    controller: (operator: string) => string;
    retention: (pendingHours: number) => string;
    meta: (operator: string) => string;
    policy: (operator: string) => string;
  };
}

/** "2 hours" in Hebrew ("שעתיים"). */
const heHours = (h: number) => (h === 2 ? 'שעתיים' : `${h} שעות`);

export const FRONT_DOOR: Record<PortalLang, FrontDoorStrings> = {
  he: {
    situations: {
      prospect: {
        title: 'בואו לראות את הדירה מקרוב',
        lead: 'השאירו מייל, ונשלח לכם קישור אישי: מה כלול בדירות, תיאום סיור בזמן שנוח לכם ודרך ישירה לצוות.',
      },
      resident: {
        title: 'הקישור האישי שלכם לפורטל',
        lead: 'הזינו את המייל שמסרתם לצוות הדיור, ונשלח אליו קישור שפותח את הפורטל.',
      },
      expired: {
        title: 'הקישור הקודם נסגר',
        lead: 'לשמירה על הפרטים שלכם, הפורטל נסגר אחרי זמן מה. הזינו את המייל ונשלח לכם קישור חדש.',
      },
      invalid: {
        title: 'הקישור הזה כבר לא פעיל',
        lead: 'קישור אישי פג אחרי זמן מה, או כשנשלח קישור חדש יותר. הזינו את המייל ונשלח לכם חדש.',
      },
    },
    or: 'או',
    googleErrors: {
      RESIDENT_NOT_INVITED: 'כתובת ה-Google הזו לא מוכרת לצוות הדיור. הזינו כאן את המייל שמסרתם להם, ונשלח אליו קישור.',
      RESIDENT_USE_EMAIL_LINK: 'הכתובת הזו מוכרת ביותר ממקום מגורים אחד. הזינו אותה כאן, ונשלח קישור לכל מקום בנפרד.',
    },
    googleFailed: 'זה לא הצליח עם Google. נסו שוב, או הזינו כאן את המייל ונשלח לכם קישור.',
    network: 'אין חיבור לאינטרנט כרגע. מה שמילאת לא נמחק; נסו שוב בעוד רגע.',
    tooMany: 'כבר שלחנו כמה קישורים בשעה האחרונה. הציצו במייל, או נסו שוב בעוד כשעה.',
    botFailed: 'בדיקת האבטחה לא עברה. נסו שוב.',
    noticeChanged: 'הודעת הפרטיות התעדכנה. עברו עליה ושלחו שוב.',
    sendFailed: 'לא הצלחנו לשלוח כרגע. נסו שוב בעוד רגע.',
    emailMissing: 'הזינו את המייל שלכם',
    emailMalformed: 'משהו חסר בכתובת. היא אמורה להיראות כך: name@example.com',
    botPending: 'רגע, בדיקת האבטחה עוד לא הסתיימה. נסו שוב בעוד שנייה.',
    sentTitle: 'בדקו את המייל',
    sentProspect: 'שלחנו קישור אישי לכתובת:',
    sentResident: 'אם הכתובת הזו מוכרת לצוות הדיור, קישור חדש כבר בדרך אליה:',
    sentOpen: 'פתחו אותו מהמייל, והכול יחכה לכם שם.',
    sentSpam: 'לא רואים אותו? הציצו בספאם או בתיקיית קידומי המכירות.',
    sentAgain: 'שלחנו שוב.',
    resendIn: (n) => `אפשר לשלוח שוב בעוד ${n} שניות`,
    resend: 'שלחו לי שוב',
    changeEmail: 'שינוי כתובת המייל',
    emailLabel: 'המייל שלכם',
    didYouMean: 'התכוונתם ל-',
    didYouMeanEnd: '?',
    marketing: (operator) => `אשמח לקבל ${withPrefix('מ', operator)} עדכונים על דירות שמתפנות (לא חובה)`,
    sending: 'שולחים…',
    submit: 'שלחו לי את הקישור',
    alreadyLive: 'כבר גרים אצלנו? אותו מייל, אותו קישור.',
    opening: 'פותחים את הקישור…',
    secure: 'הפרטים שלכם נשמרים מוצפנים ומאובטחים',
    privacy: {
      short: (operator) =>
        `המייל נשמר אצל ${operator} ומשמש לשליחת הקישור, לתיאום סיור ולמענה לפניות שלכם. TendedLoop מעבדת אותו עבורם.`,
      more: 'עוד על הפרטיות',
      controller: (operator) =>
        `${operator} הם בעלי המאגר והאחראים למידע, ו-TendedLoop מעבדת אותו בשמם. אין חובה חוקית למסור את המייל, אבל בלעדיו לא נוכל לשלוח לכם את הקישור.`,
      retention: (h) =>
        `אם הקישור לא ייפתח תוך ${heHours(h)}, המייל נמחק. אם לא תמשיכו לחוזה, הפרטים נמחקים אחרי 90 יום. עדכונים שיווקיים יישלחו רק אם סימנתם זאת, ואפשר להפסיק אותם בכל עת.`,
      meta: (operator) =>
        `רק אם סימנתם עדכונים: כדי למדוד את המודעות שלהם, ${operator} עשויים לדווח ל-Meta על הפנייה שלכם, עם כתובת ה-IP, סוג הדפדפן והמייל בצורה מגובבת (hash) שאי אפשר לקרוא. בלי הסימון, שום דבר לא נשלח ל-Meta.`,
      policy: (operator) => `מדיניות הפרטיות של ${operator}`,
    },
  },
  ar: {
    situations: {
      prospect: {
        title: 'تعالوا لرؤية الشقة عن قرب',
        lead: 'اتركوا بريدكم الإلكتروني، وسنرسل إليكم رابطًا شخصيًا: ما تشمله الشقق، وحجز جولة في الوقت المناسب لكم، وتواصل مباشر مع الفريق.',
      },
      resident: {
        title: 'رابطكم الشخصي إلى البوابة',
        lead: 'أدخلوا البريد الإلكتروني الذي قدّمتموه لفريق السكن، وسنرسل إليه رابطًا يفتح البوابة.',
      },
      expired: {
        title: 'أُغلق الرابط السابق',
        lead: 'حفاظًا على بياناتكم، تُغلق البوابة بعد فترة. أدخلوا بريدكم الإلكتروني وسنرسل إليكم رابطًا جديدًا.',
      },
      invalid: {
        title: 'هذا الرابط لم يعد فعّالًا',
        lead: 'تنتهي صلاحية الرابط الشخصي بعد فترة، أو عند إرسال رابط أحدث. أدخلوا بريدكم الإلكتروني وسنرسل إليكم رابطًا جديدًا.',
      },
    },
    or: 'أو',
    googleErrors: {
      RESIDENT_NOT_INVITED:
        'عنوان Google هذا غير معروف لفريق السكن. أدخلوا هنا البريد الإلكتروني الذي قدّمتموه لهم، وسنرسل إليه رابطًا.',
      RESIDENT_USE_EMAIL_LINK: 'هذا العنوان معروف في أكثر من مكان سكن. أدخلوه هنا، وسنرسل رابطًا لكل مكان على حدة.',
    },
    googleFailed: 'لم ينجح الدخول عبر Google. حاولوا مرة أخرى، أو أدخلوا بريدكم الإلكتروني هنا وسنرسل إليكم رابطًا.',
    network: 'لا يوجد اتصال بالإنترنت حاليًا. لم يُحذف ما أدخلتموه، فحاولوا مرة أخرى بعد قليل.',
    tooMany: 'أرسلنا عدة روابط خلال الساعة الأخيرة. تفقّدوا بريدكم الإلكتروني، أو حاولوا مرة أخرى بعد نحو ساعة.',
    botFailed: 'تعذّر اجتياز فحص الأمان. حاولوا مرة أخرى.',
    noticeChanged: 'تم تحديث إشعار الخصوصية. اطّلعوا عليه وأرسلوا مرة أخرى.',
    sendFailed: 'تعذّر الإرسال الآن. حاولوا مرة أخرى بعد قليل.',
    emailMissing: 'أدخلوا بريدكم الإلكتروني',
    emailMalformed: 'يبدو أن العنوان ناقص. يجب أن يكون بهذا الشكل: name@example.com',
    botPending: 'لحظة، فحص الأمان لم ينتهِ بعد. حاولوا مرة أخرى بعد ثانية.',
    sentTitle: 'تفقّدوا بريدكم الإلكتروني',
    sentProspect: 'أرسلنا رابطًا شخصيًا إلى العنوان:',
    sentResident: 'إذا كان فريق السكن يعرف هذا العنوان، فرابط جديد في طريقه إليه:',
    sentOpen: 'افتحوه من البريد الإلكتروني، وستجدون كل شيء بانتظاركم هناك.',
    sentSpam: 'لا ترونه؟ ألقوا نظرة على مجلد الرسائل غير المرغوب فيها (Spam) أو العروض الترويجية.',
    sentAgain: 'أرسلناه مرة أخرى.',
    resendIn: (n) => `يمكن الإرسال مرة أخرى بعد ${arCount(n, 'ثانية واحدة', 'ثانيتين', 'ثوانٍ', 'ثانية')}`,
    resend: 'أرسلوا لي مرة أخرى',
    changeEmail: 'تغيير البريد الإلكتروني',
    emailLabel: 'بريدكم الإلكتروني',
    didYouMean: 'هل قصدتم ',
    didYouMeanEnd: '؟',
    marketing: (operator) => `يسعدني أن أتلقى من ${operator} تحديثات عن الشقق التي تتوفّر (اختياري)`,
    sending: 'جارٍ الإرسال…',
    submit: 'أرسلوا لي الرابط',
    alreadyLive: 'تسكنون عندنا بالفعل؟ البريد نفسه، والرابط نفسه.',
    opening: 'جارٍ فتح الرابط…',
    secure: 'بياناتكم محفوظة مشفّرة وبأمان',
    privacy: {
      short: (operator) =>
        `يُحفظ البريد الإلكتروني لدى ${operator} ويُستخدم لإرسال الرابط وتنسيق جولة والرد على استفساراتكم. وتعالجه TendedLoop نيابةً عنه.`,
      more: 'المزيد عن الخصوصية',
      controller: (operator) =>
        `${operator} هو صاحب قاعدة البيانات والمسؤول عن المعلومات، وتعالجها TendedLoop نيابةً عنه. لا يُلزمكم القانون بتقديم البريد الإلكتروني، لكن من دونه لا يمكننا إرسال الرابط إليكم.`,
      retention: (h) =>
        `إذا لم يُفتح الرابط خلال ${arCount(h, 'ساعة واحدة', 'ساعتين', 'ساعات', 'ساعة')}، يُحذف البريد الإلكتروني. وإذا لم توقّعوا عقدًا، تُحذف بياناتكم بعد 90 يومًا. لن تصلكم تحديثات تسويقية إلا إذا وضعتم علامة في خانة التحديثات، ويمكنكم إيقافها في أي وقت.`,
      meta: (operator) =>
        `فقط إذا وضعتم علامة في خانة التحديثات: لقياس إعلاناته، قد يُبلغ ${operator} شركة Meta عن طلبكم، مع عنوان IP ونوع المتصفح، والبريد الإلكتروني بصيغة مُرمّزة (hash) لا يمكن قراءتها. من دون هذه العلامة، لا يُرسَل أي شيء إلى Meta.`,
      policy: (operator) => `سياسة الخصوصية لدى ${operator}`,
    },
  },
  en: {
    situations: {
      prospect: {
        title: 'Come and see the home up close',
        lead: 'Leave your email and we will send you a personal link: what the homes include, a tour at a time that suits you, and a direct line to the team.',
      },
      resident: {
        title: 'Your personal link to the portal',
        lead: 'Enter the email you gave the housing team, and we will send it a link that opens the portal.',
      },
      expired: {
        title: 'Your earlier link has closed',
        lead: 'To keep your details safe, the portal closes after a while. Enter your email and we will send you a new link.',
      },
      invalid: {
        title: 'This link no longer works',
        lead: 'A personal link stops working after a while, or when a newer one is sent. Enter your email and we will send you a new one.',
      },
    },
    or: 'or',
    googleErrors: {
      RESIDENT_NOT_INVITED:
        'The housing team does not know this Google address. Enter the email you gave them here, and we will send it a link.',
      RESIDENT_USE_EMAIL_LINK:
        'This address is known at more than one place. Enter it here and we will send a link for each place.',
    },
    googleFailed: 'That did not work with Google. Try again, or enter your email here and we will send you a link.',
    network: 'No internet connection right now. Nothing you entered was lost; try again in a moment.',
    tooMany: 'We already sent a few links in the last hour. Check your email, or try again in about an hour.',
    botFailed: 'The security check did not pass. Please try again.',
    noticeChanged: 'The privacy notice was updated. Please read it and send again.',
    sendFailed: 'We could not send right now. Please try again in a moment.',
    emailMissing: 'Enter your email',
    emailMalformed: 'Something is missing in the address. It should look like this: name@example.com',
    botPending: 'One moment, the security check has not finished. Try again in a second.',
    sentTitle: 'Check your email',
    sentProspect: 'We sent a personal link to:',
    sentResident: 'If the housing team knows this address, a new link is on its way to it:',
    sentOpen: 'Open it from the email, and everything will be waiting for you there.',
    sentSpam: 'Not there? Check your spam or promotions folder.',
    sentAgain: 'Sent again.',
    resendIn: (n) => `You can send again in ${n} ${n === 1 ? 'second' : 'seconds'}`,
    resend: 'Send it again',
    changeEmail: 'Change the email',
    emailLabel: 'Your email',
    didYouMean: 'Did you mean ',
    didYouMeanEnd: '?',
    marketing: (operator) => `Email me news from ${operator} about homes that become available (optional)`,
    sending: 'Sending…',
    submit: 'Send me the link',
    alreadyLive: 'Already live with us? Same email, same link.',
    opening: 'Opening the link…',
    secure: 'Your details are kept encrypted and secure',
    privacy: {
      short: (operator) =>
        `${operator} keeps your email and uses it to send the link, arrange a tour and answer you. TendedLoop processes it on their behalf.`,
      more: 'More about privacy',
      controller: (operator) =>
        `${operator} is the controller of this data and responsible for it, and TendedLoop processes it on their behalf. You are under no legal duty to give your email, but without it we cannot send you the link.`,
      retention: (h) =>
        `If the link is not opened within ${h === 1 ? '1 hour' : `${h} hours`}, the email is deleted. If you do not go on to a lease, your details are deleted after 90 days. Marketing news is sent only if you ticked the box, and you can stop it at any time.`,
      meta: (operator) =>
        `Only if you ticked the news box: to measure its ads, ${operator} may tell Meta about your request, with your IP address, browser type, and your email hashed in a form no one can read. Without the tick, nothing is sent to Meta.`,
      policy: (operator) => `${operator} privacy policy`,
    },
  },
};
